import React, { useContext, useEffect, useState } from "react";
import { Layout, Row, Col, Select, DatePicker, Spin, Button, Form } from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import { TDropdownOptions, TResponseModel, TUser } from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import {
  GetCustMappedPartnerList,
  GetCustomerList,
  GetDashboardDailyTicketCount,
  GetServicePartnerList,
} from "../../../utils/api.constant";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import dayjs, { Dayjs } from "dayjs";
import ParameterTable from "./helperComponents/parameterTable";
import SlaGauge from "./helperComponents/slaGauge";
import HardwareIssues from "./helperComponents/hardwareIssues";
import PartnerDataTable from "./helperComponents/partnerDataTable";
import { CloseOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { TCallOpenData } from "../../../types";
import { STAKE_HOLDERS } from "../../../utils/constants";
import { UserContext } from "../../../context/user";
import DateUtils from "../../../utils/date";
import CurrentDayTickets from "./helperComponents/currentDayTickets";
import styles from "./dashboard.module.css";

const { Header, Content } = Layout;

const initialDailyTicketCount = {
  callOpenTotal: {
    serviceCount: 1,
    installCount: 0,
    othersCount: 0,
  },
  callOpenAttended: {
    serviceCount: 0,
    installCount: 0,
    othersCount: 0,
  },
  callOpenDealerDependency: {
    serviceCount: 0,
    installCount: 0,
    othersCount: 0,
  },
  callOpenHoldSpares: {
    serviceCount: 0,
    installCount: 0,
    othersCount: 0,
  },
  callOpenHoldAMCApproval: {
    serviceCount: 0,
    installCount: 0,
    othersCount: 0,
  },
  callOpenWithinTAT: {
    serviceCount: 1,
    installCount: 0,
    othersCount: 0,
  },
  callOpenOutOfTAT: {
    serviceCount: 0,
    installCount: 0,
    othersCount: 0,
  },
  callOpenPendingUnattended: {
    serviceCount: 1,
    installCount: 0,
    othersCount: 0,
  },
};

const Dashboard: React.FC = () => {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [partnerOptions, setPartnerOptions] = useState<TDropdownOptions[]>([
    { value: "ALL", label: "All" },
  ]);
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format("DD-MMM-YYYY")
  );
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<string[]>([]);
  const [dailyTicketCount, setDailyTicketCount] = useState<TCallOpenData>(
    initialDailyTicketCount
  );
  const [isDailyTicketLoading, setDailyTicketLoading] =
    useState<boolean>(false);
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [parameterTableData, setParameterTableData] = useState<any[]>([]);
  const [partnerTableData, setPartnerTableData] = useState<any[]>([]);
  const [slaData, setSlaData] = useState<any[]>([]);
  const [hardwareData, setHardwareData] = useState<any>([]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setActiveUser(context?.user);
      if (context?.user?.stakeHolderName === STAKE_HOLDERS.IDEX) {
        setSelectedPartners(["ALL"]);
        setSelectedCustomers(["ALL"]);
      }
    }, 100);

    return () => window.clearTimeout(timeoutId); // Cleanup on unmount or dependency change
  }, [context]);

  useEffect(() => {
    if (selectedCustomers.includes("ALL")) {
      getServicePartnerList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedCustomers)]);

  const handleDateChange = (
    date: Dayjs | null,
    dateString: string | string[]
  ) => {
    if (!date) {
      setSelectedDate(""); // Clear the date when the picker is cleared
    } else if (Array.isArray(dateString)) {
      console.warn("Unexpected dateString as array:", dateString); // Handle unexpected array case
    } else {
      setSelectedDate(dateString); // Update state with formatted date
      console.log("Selected Date:", dateString);
    }
  };

  const getCustomerList = async () => {
    try {
      let requestPayload;
      if (context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER) {
        requestPayload = {
          data: {
            pageSize: -1,
            currentPage: 1,
            servicePartnerId: context?.user?.orgId,
            isEngineerExclusiveView: 1,
          },
        };
      } else {
        requestPayload = {
          data: {
            pageSize: -1,
            currentPage: 1,
          },
        };
      }
      const response: TResponseModel = await post(
        GetCustomerList,
        requestPayload
      );
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const customerOptions = response.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));

        setCustomerOptions([
          ...customerOptions,
          { label: "All", value: "ALL" },
        ]);

        setSelectedCustomers(
          activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
            ? ["ALL"]
            : [response.data[0].id]
        );
      } else {
        onShowToast({
          type: "error",
          title: <CloseOutlined />,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchMappedPartners = async (customerIds: string[]) => {
    try {
      let allPartners: TDropdownOptions[] = [];

      for (const customerId of customerIds) {
        const payload = { data: { customerId, pageSize: -1, currentPage: 1 } };
        const response: TResponseModel = await post(
          GetCustMappedPartnerList,
          payload
        );

        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          allPartners = [
            ...allPartners,
            ...response.data.map((item: any) => ({
              label: item.name,
              value: item.id,
            })),
          ];
        }
      }

      // Remove duplicates while maintaining order
      const uniquePartners = Array.from(
        new Map(allPartners.map((item) => [item.value, item])).values()
      );

      // Ensure "ALL" option is at the very end
      uniquePartners.push({ value: "ALL", label: "All" });

      setPartnerOptions(uniquePartners);

      setSelectedPartners(
        activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
          ? ["ALL"]
          : ([uniquePartners[0]?.value] as any)
      );
    } catch (error) {
      console.error("Error fetching mapped partners:", error);
    }
  };

  const getServicePartnerList = async () => {
    try {
      let requestPayload = {
        data: {
          pageSize: -1,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
        GetServicePartnerList,
        requestPayload
      );
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const partinerOptions = response.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));

        setPartnerOptions([...partinerOptions, { label: "All", value: "ALL" }]);
      } else {
        onShowToast({
          type: "error",
          title: <CloseOutlined />,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchDailyTicketCount = async () => {
    if (selectedCustomers.length && selectedPartners.length) {
      try {
        setDailyTicketLoading(true);
        let payload = {
          data: {
            viewDate: DateUtils.getDateStringWithOutTimezone(
              new Date().toDateString()
            ),
            IsAllCustomerRequest: selectedCustomers?.includes("ALL") ? 1 : 0,
            IsAllServicePartnerRequest: selectedPartners?.includes("ALL")
              ? 1
              : 0,
            customerId: selectedCustomers?.includes("ALL")
              ? customerOptions
                  ?.filter((option) => option.value !== "ALL")
                  .map((option) => option.value)
              : selectedCustomers,
            servicePartnerId: selectedPartners?.includes("ALL")
              ? partnerOptions
                  ?.filter((option) => option.value !== "ALL")
                  .map((option) => option.value)
              : selectedPartners,
          },
        };

        const response: TResponseModel = await post(
          GetDashboardDailyTicketCount,
          payload
        );
        const { data, dataResponse } = response;
        const { description, returnCode } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setDailyTicketCount({ ...data });
          setDailyTicketLoading(false);
        } else {
          onShowToast({
            type: "error",
            title: <CloseOutlined />,
            content: description,
          });
          setDailyTicketLoading(false);
        }
      } catch (error) {
        console.error("Error fetching mapped partners:", error);
      } finally {
        setDailyTicketLoading(false);
      }
    }
  };

  useEffect(() => {
    getCustomerList();
  }, []);

  useEffect(() => {
    fetchDailyTicketCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedCustomers), JSON.stringify(selectedPartners)]);

  useEffect(() => {
    if (selectedCustomers.length > 0 && !selectedCustomers.includes("ALL")) {
      fetchMappedPartners(selectedCustomers);
    }
    // else {
    //   setPartnerOptions([]);
    //   setSelectedPartners([]);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedCustomers)]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const parameterSheetData = [
      [
        "Parameter",
        "Today Service",
        "Today Install",
        "Today Others",
        "Last 7 Days Service",
        "Last 7 Days Install",
        "Last 7 Days Others",
        "This Month Service",
        "This Month Install",
        "This Month Others",
      ],
      ...parameterTableData.map((data, index) => [
        ["Call logged", "Closed within TAT", "Closed out of TAT"][index],
        data?.todayService,
        data?.todayInstall,
        data?.todayOthers,
        data?.last7Service,
        data?.last7Install,
        data?.last7Others,
        data?.monthService,
        data?.monthInstall,
        data?.monthOthers,
      ]),
    ];

    const wsParameterTable = XLSX.utils.aoa_to_sheet(parameterSheetData);
    XLSX.utils.book_append_sheet(wb, wsParameterTable, "Parameter Table");

    console.log("partnerTableData", partnerTableData);
    const partnerSheetData = [
      [
        "Service Partner",
        "Productivity",
        "Present count",
        "Absent count",
        "Avg time local",
        "Avg time up country",
        "Avg time remote",
      ],
      ...partnerTableData.map((data, index) => [
        data?.servicePartnerName,
        data?.productivityPercent,
        data?.presentCount,
        data?.absentCount,
        data?.avgTimeLocal,
        data?.avgTimeUpCountry,
        data?.avgTimeRemote,
      ]),
    ];
    const wsPartnerTable = XLSX.utils.aoa_to_sheet(partnerSheetData);
    XLSX.utils.book_append_sheet(wb, wsPartnerTable, "Partner Table");

    const slaDataNew = XLSX.utils.json_to_sheet([slaData]);
    XLSX.utils.book_append_sheet(wb, slaDataNew, "SLA Gauge");

    const issueTypes = Object.keys(hardwareData.today);
    const sheetData = [["Issue", "Today", "Last 7 Days", "This Month"]];
    issueTypes.forEach((issue) => {
      sheetData.push([
        issue,
        hardwareData.today[issue],
        hardwareData.lastSevenDay[issue],
        hardwareData.inThisMonth[issue],
      ]);
    });
    const hardwareDataNew = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, hardwareDataNew, "Hardware Issues");
    const dailySheetData = [
      ["Type", "Service", "Install", "Others"],
      [
        "Total",
        dailyTicketCount.callOpenTotal.serviceCount,
        dailyTicketCount.callOpenTotal.installCount,
        dailyTicketCount.callOpenTotal.othersCount,
      ],
      [
        "Attended",
        dailyTicketCount.callOpenAttended.serviceCount,
        dailyTicketCount.callOpenAttended.installCount,
        dailyTicketCount.callOpenAttended.othersCount,
      ],
      [
        "Dealer Dependency",
        dailyTicketCount.callOpenDealerDependency.serviceCount,
        dailyTicketCount.callOpenDealerDependency.installCount,
        dailyTicketCount.callOpenDealerDependency.othersCount,
      ],
      [
        "Hold Spares",
        dailyTicketCount.callOpenHoldSpares.serviceCount,
        dailyTicketCount.callOpenHoldSpares.installCount,
        dailyTicketCount.callOpenHoldSpares.othersCount,
      ],
      [
        "Hold AMC Approval",
        dailyTicketCount.callOpenHoldAMCApproval.serviceCount,
        dailyTicketCount.callOpenHoldAMCApproval.installCount,
        dailyTicketCount.callOpenHoldAMCApproval.othersCount,
      ],
      [
        "Pending Unattended",
        dailyTicketCount.callOpenPendingUnattended.serviceCount,
        dailyTicketCount.callOpenPendingUnattended.installCount,
        dailyTicketCount.callOpenPendingUnattended.othersCount,
      ],
      [
        "Within TAT",
        dailyTicketCount.callOpenWithinTAT.serviceCount,
        dailyTicketCount.callOpenWithinTAT.installCount,
        dailyTicketCount.callOpenWithinTAT.othersCount,
      ],
      [
        "Out of TAT",
        dailyTicketCount.callOpenOutOfTAT.serviceCount,
        dailyTicketCount.callOpenOutOfTAT.installCount,
        dailyTicketCount.callOpenOutOfTAT.othersCount,
      ],
    ];
    const dailyCountNew = XLSX.utils.aoa_to_sheet(dailySheetData);
    XLSX.utils.book_append_sheet(wb, dailyCountNew, "Daily Ticket Count");

    XLSX.writeFile(
      wb,
      `dashboard_data_${DateUtils.format(new Date(), "yyyy-mm-dd")}.xlsx`
    );
  };

  return (
    <Layout
      style={{ height: "91vh", overflow: "scroll", padding: "0 10px" }}
      id="dashboard"
    >
      <Form
        // initialValues={defaultValues}
        layout="vertical"
      >
        <Row
          gutter={16}
          className=""
          style={{ padding: "0 18px", alignItems: "center" }}
        >
          <Col span={2}>
            <h3>Dashboard</h3>
          </Col>
          <Col span={2}>
            <Form.Item label={<span className={styles.customLabel}>Date</span>}>
              <DatePicker
                format="DD-MMM-YYYY"
                value={selectedDate ? dayjs(selectedDate, "DD-MMM-YYYY") : null} // Set value as null if date is cleared
                onChange={handleDateChange}
                placeholder="Select Date"
                style={{ width: "10%" }}
              />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item
              label={<span className={styles.customLabel}>Customer</span>}
            >
              <Select
                disabled={
                  activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                }
                // defaultValue={
                //   activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
                //     ? ["ALL"]
                //     : [customerOptions[0]?.value as any]
                // }
                options={customerOptions}
                onChange={(value) => {
                  if (value?.[value.length - 1] === "ALL") {
                    setSelectedCustomers(["ALL"]); // Only "ALL" should be selected
                  } else {
                    setSelectedCustomers(value.filter((v) => v !== "ALL")); // Remove "ALL" if other options are selected
                  }
                }}
                mode="multiple"
                value={selectedCustomers}
                placeholder="Customer Name"
                // style={{ width: "45%" }}
                // tagRender={(props) => {
                //   const { label } = props;
                //   return <span>{label}</span>; // Removes cross icon
                // }}

                allowClear={false} // Disables clearing selection
                filterOption={(input, option: any) => {
                  return option?.label
                    ? option.label.toLowerCase().includes(input.toLowerCase())
                    : false;
                }}
              />
            </Form.Item>
          </Col>

          <Col span={9}>
            <Form.Item
              label={
                <span className={styles.customLabel}>Service Partner</span>
              }
            >
              <Select
                disabled={
                  activeUser?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
                    ? true
                    : false
                }
                // defaultValue={
                //   activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX
                //     ? ["ALL"]
                //     : [partnerOptions[0]?.value as any]
                // }
                filterOption={(input, option: any) => {
                  return option?.label
                    ? option.label.toLowerCase().includes(input.toLowerCase())
                    : false;
                }}
                options={partnerOptions}
                onChange={(value, option) => {
                  if (value?.[value.length - 1] === "ALL") {
                    setSelectedPartners(["ALL"]); // Only "ALL" should be selected
                  } else {
                    setSelectedPartners(value.filter((v) => v !== "ALL")); // Remove "ALL" if other options are selected
                  }
                }}
                mode="multiple"
                value={selectedPartners}
                placeholder="Service Partner Name"
                // style={{ width: "45%" }}
              />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Button onClick={exportToExcel} type="primary">
              Download
            </Button>
          </Col>
        </Row>
      </Form>

      <Content style={{ padding: "0 16px" }}>
        <Row gutter={[16, 16]}>
          {/* Left Column - First Three Rows */}
          <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={24}>
                <ParameterTable
                  viewDate={selectedDate}
                  customerIds={selectedCustomers}
                  servicePartnerIds={selectedPartners}
                  customerOptions={customerOptions}
                  partnerOptions={partnerOptions}
                  onDataFetch={setParameterTableData}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
              <Col xs={8}>
                <SlaGauge
                  viewDate={selectedDate}
                  customerIds={selectedCustomers}
                  servicePartnerIds={selectedPartners}
                  customerOptions={customerOptions}
                  partnerOptions={partnerOptions}
                  onDataFetch={setSlaData}
                />
              </Col>
              <Col xs={16}>
                <HardwareIssues
                  viewDate={selectedDate}
                  customerIds={selectedCustomers}
                  servicePartnerIds={selectedPartners}
                  customerOptions={customerOptions}
                  partnerOptions={partnerOptions}
                  onDataFetch={setHardwareData}
                />
              </Col>
            </Row>

            {/* <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
              <Col xs={24} lg={24}>
               
              </Col>
            </Row> */}
          </Col>

          {/* Right Column - Dashboard Cards (stacked from top to bottom) */}
          <Col
            xs={24}
            lg={8}
            // style={{
            //   background: "var(--bs-primary-dark)",
            //   padding: "10px 0",
            // }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={24}>
                <CurrentDayTickets
                  dailyTicketCount={dailyTicketCount}
                  isDailyTicketLoading={isDailyTicketLoading}
                />
              </Col>
              {/* <div style={{ margin: "0 10px", paddingTop: "5px" }}>
                  <h2 style={{ color: "#FFF" }}>Today's Call</h2>
                </div> */}

              {/* <div style={{ padding: "10px 0", width: "100%" }}>
                {isDailyTicketLoading ? (
                  <Col xs={24}>
                    <Spin
                      tip={
                        <span
                          style={{
                            color: "#fff",
                            fontFamily: "Montserrat-Regular",
                          }}
                        ></span>
                      }
                      size="large"
                    >
                      <div style={contentStyle} />
                    </Spin>
                  </Col>
                ) : (
                  <>
                    {/* Add all the DashboardCardComponent inside the right column */}
              {/* {dailyTicketCount &&
                      Object?.entries(dailyTicketCount).map(
                        ([
                          key,
                          { serviceCount, installCount, othersCount },
                        ]) => (
                          <Col
                            xs={24}
                            key={key}
                            style={{ marginTop: "0.55rem" }}
                          >
                            <DashboardCardComponent
                              cardTitle={cardTitleMapper(key) || ""}
                              service={serviceCount}
                              install={installCount}
                              others={othersCount}
                            />
                          </Col>
                        )
                      )}
                  </>
                )} */}
              {/* </div> */}
            </Row>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
          {/* Left Column - First Three Rows */}
          <Col xs={24} lg={24}>
            <PartnerDataTable
              viewDate={selectedDate}
              customerIds={selectedCustomers}
              servicePartnerIds={selectedPartners}
              customerOptions={customerOptions}
              partnerOptions={partnerOptions}
              onDataFetch={setPartnerTableData}
            />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default Dashboard;
